import React, { useState, useMemo } from 'react';
import {
  Card,
  makeStyles,
  Grid,
  Typography,
  Collapse,
  IconButton,
} from '@material-ui/core';
import clsx from 'clsx';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import colors from '../../../theme/colors';

type StyleProps = {
  clickable: boolean;
  backColor: string;
  background: string;
  minWidth: number;
  width: string;
};

const useStyles = makeStyles({
  cardRoot: (props: StyleProps) => ({
    minWidth: props.minWidth,
    width: props.width,
    borderRadius: 10,
    padding: 20,
    cursor: props.clickable ? 'pointer' : 'auto',
    backgroundColor: props.backColor,
    background: props.background,
  }),
  regular: {
    boxShadow: '0px 5px 5px rgba(0, 0, 0, 0.08)',
  },
  raised: {
    boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.4)',
  },
  textWithPadding: {
    marginBottom: 10,
    transition: 'margin-bottom 0.5s',
  },
  textWithoutPadding: {
    marginBottom: 0,
    transition: 'margin-bottom 0.5s',
  },
  disabledTitle: {
    color: colors.silver,
  },
});

export type DefaultCardProps = {
  /** Optional card title, with appropiate styles */
  title?: React.ReactNode;
  /** Optional card subtitle, with appropiate styles */
  smallSubtitle?: React.ReactNode;
  /** Element's children */
  children?: React.ReactNode;
  /** If true, the card can be collapsed */
  collapsible?: boolean;
  /** If true, the card will start collapsed */
  startCollapsed?: boolean;
  /** If defined, the method will be called when the card is clicked */
  onClick?: () => void;
  /** Renders a card with the disabled variant */
  disabled?: boolean;
  /** Renders a card with the raised variant */
  raised?: boolean;
  /** Overwrites the internal collapse logic with an external value */
  forceCollapsed?: boolean;
  /** If provided, trying to expand/collapse will call this method instead of updating the internal state */
  onForceCollapsedUpdate?: () => void;
  /**
   * If provided, this view will be rendered on top of the collapsible content, and will not be hidden
   * when the card is collapsed.
   */
  nonCollapsibleElement?: React.ReactNode;
  forcedMinHeight?: number;
  id?: string;
  backColor?: string;
  background?: string;
  minWidth?: number;
  width?: string;
  arrowColor?: string;
  cardStyles?: React.CSSProperties;
  collapseIconId?: string;
};

type IsCollapsedProps = {
  internalCollapseReference: boolean;
  collapsible: boolean;
  forceCollapsed?: boolean;
};
function isCollapsed({
  internalCollapseReference,
  collapsible,
  forceCollapsed,
}: IsCollapsedProps): boolean {
  if (!collapsible) {
    return false;
  }

  if (typeof forceCollapsed !== 'undefined') {
    return forceCollapsed;
  }
  return internalCollapseReference;
}

type ShouldShowIconProps = {
  collapsible: boolean;
  forceCollapsed?: boolean;
  onForceCollapsedUpdate?: () => void;
};
function shouldShowIcon({
  collapsible,
  forceCollapsed,
  onForceCollapsedUpdate,
}: ShouldShowIconProps): boolean {
  if (collapsible) {
    return (
      (typeof forceCollapsed !== 'undefined' &&
        typeof onForceCollapsedUpdate !== 'undefined') ||
      (typeof forceCollapsed === 'undefined' &&
        typeof onForceCollapsedUpdate === 'undefined')
    );
  }
  return false;
}

function setTitle(
  objTitle: React.ReactNode,
  disabled: boolean,
  collapsible: boolean,
  collapsed: boolean,
  classes: Record<string, string>,
): React.ReactElement {
  let titleElement;
  if (typeof objTitle === 'string') {
    titleElement = (
      <Typography
        data-testid="cardTitle"
        variant="body1"
        className={clsx(
          disabled ? classes.disabledTitle : '',
          collapsible && collapsed
            ? classes.textWithoutPadding
            : classes.textWithPadding,
        )}
      >
        {objTitle}
      </Typography>
    );
  } else {
    titleElement = <> {objTitle} </>;
  }
  return titleElement;
}

function setSmallSubtitle(objSubtitle: React.ReactNode): React.ReactElement {
  let subtitleElement;
  if (typeof objSubtitle === 'string') {
    subtitleElement = <Typography variant="caption">{objSubtitle}</Typography>;
  } else {
    subtitleElement = <>{objSubtitle}</>;
  }
  return subtitleElement;
}

function isUndefined(valueToCheck: React.ReactNode): boolean {
  return typeof valueToCheck === 'undefined';
}

const DefaultCard = ({
  children,
  title,
  smallSubtitle,
  collapsible = false,
  startCollapsed = false,
  onClick,
  disabled = false,
  raised = false,
  forceCollapsed,
  onForceCollapsedUpdate,
  nonCollapsibleElement,
  forcedMinHeight,
  id,
  backColor,
  background,
  minWidth = 275,
  width,
  arrowColor,
  cardStyles,
  collapseIconId,
}: DefaultCardProps): JSX.Element => {
  const [collapsed, toggleCollapsed] = useState(startCollapsed);
  const classes = useStyles({
    clickable: typeof onClick !== 'undefined',
    backColor,
    background,
    minWidth,
    width,
  });

  const shouldShowCollapsed = useMemo(
    () =>
      isCollapsed({
        internalCollapseReference: collapsed,
        collapsible,
        forceCollapsed,
      }),
    [collapsed, collapsible, forceCollapsed],
  );

  const icon = useMemo((): React.ReactNode => {
    return shouldShowCollapsed ? (
      <KeyboardArrowDownIcon htmlColor={arrowColor} />
    ) : (
      <KeyboardArrowUpIcon htmlColor={arrowColor} />
    );
  }, [shouldShowCollapsed]);

  function handleCollapseClick(event: React.SyntheticEvent): void {
    event.stopPropagation();
    if (!isUndefined(forceCollapsed) && !isUndefined(onForceCollapsedUpdate)) {
      onForceCollapsedUpdate();
    } else {
      toggleCollapsed(!collapsed);
    }
  }

  return (
    <Card
      className={clsx(
        classes.cardRoot,
        raised ? classes.raised : classes.regular,
      )}
      style={{
        minHeight: forcedMinHeight || 'auto',
        ...cardStyles,
      }}
      elevation={0}
      onClick={onClick}
      id={id}
    >
      {(!isUndefined(title) || collapsible) && (
        <Grid container>
          <Grid item xs>
            {setTitle(title, disabled, collapsible, collapsed, classes)}
          </Grid>
          {shouldShowIcon({
            collapsible,
            forceCollapsed,
            onForceCollapsedUpdate,
          }) && (
            <Grid item>
              <IconButton
                data-testid="collapse-button"
                onClick={handleCollapseClick}
                id={collapseIconId}
              >
                {icon}
              </IconButton>
            </Grid>
          )}
        </Grid>
      )}
      {nonCollapsibleElement}
      <Collapse data-testid="content" in={!shouldShowCollapsed}>
        {!isUndefined(smallSubtitle) && (
          <div className={classes.textWithPadding}>
            {setSmallSubtitle(smallSubtitle)}
          </div>
        )}
        {children}
      </Collapse>
    </Card>
  );
};

export default DefaultCard;
